// src/api/DidacticRoutesAPI.js

import { ENV } from "../Utils";
import { apiFetch } from "../Utils/apiFetch";

export class DidacticRoutesAPI {
  baseApi = ENV.BASE_API;

  // 1) Crear una nueva Ruta Didáctica
  //    Endpoint: POST /create_ruta/:teacherId/:institutionId
  //    Body (JSON) con { nameRute, nameinstitution, nameTeacher, description, classnumber, scenesRute? }
  async createDidacticRoute(accessToken, teacherId, institutionId, data) {
    try {
      const url = `${this.baseApi}/create_ruta/${teacherId}/${institutionId}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, route, teacher, updatedStudentsCount }
    } catch (error) {
      console.error("Error en createDidacticRoute:", error);
      throw error;
    }
  }

  // 2) Obtener TODAS las rutas
  //    Endpoint: GET /getall_ruta
  async getAllRoutes(accessToken) {
    try {
      const url = `${this.baseApi}/getall_ruta`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, routes: [...] }
    } catch (error) {
      console.error("Error en getAllRoutes:", error);
      throw error;
    }
  }
  async getRouteById(accessToken, routeId) {
    try {
      const url = `${this.baseApi}/getone_ruta/${routeId}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, route: { ... } }
    } catch (error) {
      console.error("Error en getRouteById:", error);
      throw error;
    }
  }

  // 3) Obtener las rutas de un Teacher
  //    Endpoint: GET /getbyteacher_ruta/:teacherId
  async getRoutesByTeacher(accessToken, teacherId) {
    try {
      const url = `${this.baseApi}/getbyteacher_ruta/${teacherId}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, routes: [...] }
    } catch (error) {
      console.error("Error en getRoutesByTeacher:", error);
      throw error;
    }
  }

  // 4) Obtener las rutas de un Student
  //    Endpoint: GET /getbystudent_ruta/:studentId
  async getRoutesByStudent(accessToken, studentId) {
    try {
      const url = `${this.baseApi}/getbystudent_ruta/${studentId}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, routes: [...] }
    } catch (error) {
      console.error("Error en getRoutesByStudent:", error);
      throw error;
    }
  }
  // NUEVO: Obtener las escenas de una Ruta Didáctica
  // Endpoint: GET /getscenesbyroute/:routeId
  async getScenesByRoute(accessToken, routeId, page = 1, limit = 4) {
    try {
      const url = `${this.baseApi}/getscenesbyroute/${routeId}?page=${page}&limit=${limit}`;
      const params = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await apiFetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        // Ajusta si tu backend usa otro código en caso de error
        throw new Error(result.message);
      }
      // El backend devuelve { code, message, scenes: [...] }
      return result;
    } catch (error) {
      console.error("Error en getScenesByRoute:", error);
      throw error;
    }
  }
  async getRutasDidacticasByClassNumber(
    accessToken,
    institutionId,
    classNumber,
  ) {
    try {
      const url = `${this.baseApi}/institution/${institutionId}/rutas-class`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ classNumber }), // Enviamos el classNumber en el body
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      // El backend retorna { code, message, rutas: [...] }
      return result;
    } catch (error) {
      console.error("Error en getRutasDidacticasByClassNumber:", error);
      throw error;
    }
  }
  /**
   * Inserta (o reemplaza) un listado de asistentes en la ruta.
   * Endpoint: POST /insert_assistants/:routeId
   * @param {string} accessToken - Token de autenticación
   * @param {string} routeId - ID de la ruta donde se insertarán los asistentes
   * @param {string[]} assistantIds - Array con los IDs de los asistentes
   * @returns {object} { code, message, route } (según tu backend)
   */
  async insertAssistantsInRoute(accessToken, routeId, assistantIds) {
    try {
      const url = `${this.baseApi}/insert_assistants/${routeId}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assistants: assistantIds }),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message || "Error al insertar asistentes");
      }
      return result; // { code, message, route }
    } catch (error) {
      console.error("Error en insertAssistantsInRoute:", error);
      throw error;
    }
  }

  /**
   * Elimina un asistente específico del array 'assistants' en la ruta.
   * Endpoint: DELETE /remove_assistant/:routeId/:assistantId
   * @param {string} accessToken - Token de autenticación
   * @param {string} routeId - ID de la ruta
   * @param {string} assistantId - ID del asistente a eliminar
   * @returns {object} { code, message, route } (según tu backend)
   */
  async removeAssistantFromRoute(accessToken, routeId, assistantId) {
    try {
      const url = `${this.baseApi}/remove_assistant/${routeId}/${assistantId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message || "Error al eliminar asistente");
      }
      return result; // { code, message, route }
    } catch (error) {
      console.error("Error en removeAssistantFromRoute:", error);
      throw error;
    }
  }
  // 5) Actualizar una Ruta Didáctica
  //    Endpoint: PATCH /update_ruta/:routeId
  //    Body (JSON) con { nameRute?, description?, scenesRute?: [array de IDs de escenas] }
  async updateDidacticRoute(accessToken, routeId, data) {
    try {
      const url = `${this.baseApi}/update_ruta/${routeId}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, route }
    } catch (error) {
      console.error("Error en updateDidacticRoute:", error);
      throw error;
    }
  }

  // 5-bis) Entrar a la escena activa de la ruta
  //    Endpoint: POST /enter_active_scene/:routeId   Body opcional { sceneId }
  //    Devuelve { url, sceneId, expiresAt, reused }: la URL de una sala VIVA
  //    (reutiliza la guardada o crea otra si caduco a las 3h; una por clase).
  //    Con sceneId (solo profesor) activa esa escena del itinerario.
  //    El error lleva .status y .code ("no-active-scene", "busy"...).
  async enterActiveScene(accessToken, routeId, sceneId) {
    const url = `${this.baseApi}/enter_active_scene/${routeId}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sceneId ? { sceneId } : {}),
    });
    let result = null;
    try {
      result = await response.json();
    } catch (_e) {
      result = null;
    }
    if (response.status !== 200 || !result?.url) {
      const error = new Error(result?.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.code = result?.code || null;
      throw error;
    }
    return result;
  }

  // 6) Eliminar una Ruta Didáctica COMPLETA
  //    Endpoint: DELETE /delete_ruta/:routeId
  //    Esto la quita de la colección "ruta", del teacher.rutasDidacticas y de student.calification[].rutasDidacticas
  async deleteRoute(accessToken, routeId) {
    try {
      const url = `${this.baseApi}/delete_ruta/${routeId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message);
      }
      return result; // { code, message, removedFromTeacher, removedFromStudents }
    } catch (error) {
      console.error("Error en deleteRoute:", error);
      throw error;
    }
  }

  // 7) Eliminar una escena específica de la Ruta
  //    Endpoint: DELETE /delete_sceneroute/:routeId/:sceneId
  //    Esto quita solo el subdocumento { scene: sceneId } de route.scenesRute
  async deleteSceneFromRoute(accessToken, routeId, sceneId) {
    try {
      const url = `${this.baseApi}/delete_sceneroute/${routeId}/${sceneId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        // A veces el backend retorna 200 incluso si no se encontró la escena,
        // pero si definiste otro status, ajusta aquí la condición.
        throw new Error(result.message);
      }
      return result; // { code, message, route }
    } catch (error) {
      console.error("Error en deleteSceneFromRoute:", error);
      throw error;
    }
  }
  // --- Compartir una ruta ---
  async shareRoute(
    accessToken,
    teacherId,
    routeId,
    { scope, institutionId } = {},
  ) {
    try {
      const url = `${this.baseApi}/share_ruta/${teacherId}/${routeId}`;
      const body = { scope };
      if (institutionId) body.institutionId = institutionId;

      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      };

      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw new Error(result.message);
      return result; // { code, message, route }
    } catch (error) {
      console.error("Error en shareRoute:", error);
      throw error;
    }
  }

  // --- Rutas visibles para un teacher (propias + compartidas) ---
  async getVisibleRoutes(accessToken, teacherId) {
    try {
      const url = `${this.baseApi}/visible_rutas/${teacherId}`;
      const params = {
        headers: { Authorization: `Bearer ${accessToken}` },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw new Error(result.message);
      // { own:[], sharedInstitution:[], sharedGlobal:[] }
      return result;
    } catch (error) {
      console.error("Error en getVisibleRoutes:", error);
      throw error;
    }
  }

  // --- (Opcional) Obtener compartidas en un único array ---
  async getSharedRoutes(accessToken, teacherId) {
    try {
      const url = `${this.baseApi}/shared_rutas/${teacherId}`;
      const params = {
        headers: { Authorization: `Bearer ${accessToken}` },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw new Error(result.message);
      // { shared:[...], count }
      return result;
    } catch (error) {
      console.error("Error en getSharedRoutes:", error);
      throw error;
    }
  }

  // --- Adoptar una ruta compartida (agregar a Mis Itinerarios) ---
  async adoptSharedRoute(accessToken, teacherId, routeId) {
    try {
      const url = `${this.baseApi}/adopt_shared_ruta/${teacherId}/${routeId}`;
      const params = {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw new Error(result.message);
      // { code, adopted:true, route }
      return result;
    } catch (error) {
      console.error("Error en adoptSharedRoute:", error);
      throw error;
    }
  }
}
